import React, { useMemo, useState } from "react";
import { Send, Plus, Settings, Bot, MessageCirclePlus, Search, Star, MessageSquare, CircleHelp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import "../cards/message/MessageBubbleCard.css";

// Hoverable sidebar item component
function SidebarItem({ 
  children, 
  isActive = false,
  className,
}: { 
  children: React.ReactNode;
  isActive?: boolean;
  className?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={cn("flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors", className)}
      style={{ 
        backgroundColor: isActive 
          ? `hsl(var(--sidebar-accent))` 
          : isHovered 
            ? `hsl(var(--sidebar-accent) / 0.5)` 
            : "transparent",
        color: isActive 
          ? `hsl(var(--sidebar-accent-foreground))` 
          : `hsl(var(--sidebar-foreground))`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </div>
  );
}

type BrandingValues = {
  // Light theme
  primary?: string;
  primary_foreground?: string;
  secondary?: string;
  secondary_foreground?: string;
  accent?: string;
  accent_foreground?: string;
  background?: string;
  foreground?: string;
  card?: string;
  card_foreground?: string;
  muted?: string;
  muted_foreground?: string;
  border?: string;
  input?: string;
  ring?: string;
  radius?: string;
  sidebar?: string;
  sidebar_background?: string;
  sidebar_foreground?: string;
  sidebar_primary?: string;
  sidebar_primary_foreground?: string;
  sidebar_accent?: string;
  sidebar_accent_foreground?: string;
  sidebar_border?: string;
  sidebar_ring?: string;
  // Dark theme
  dark_primary?: string;
  dark_primary_foreground?: string;
  dark_secondary?: string;
  dark_secondary_foreground?: string;
  dark_accent?: string;
  dark_accent_foreground?: string;
  dark_background?: string;
  dark_foreground?: string;
  dark_card?: string;
  dark_card_foreground?: string;
  dark_muted?: string;
  dark_muted_foreground?: string;
  dark_border?: string;
  dark_input?: string;
  dark_ring?: string;
  dark_sidebar?: string;
  dark_sidebar_background?: string;
  dark_sidebar_foreground?: string;
  dark_sidebar_primary?: string;
  dark_sidebar_primary_foreground?: string;
  dark_sidebar_accent?: string;
  dark_sidebar_accent_foreground?: string;
  dark_sidebar_border?: string;
  dark_sidebar_ring?: string;
  // Fonts
  font_sans?: string;
  font_mono?: string;
};

type ChatPreviewProps = {
  values: BrandingValues;
  isDark?: boolean;
  className?: string;
};

// Helper to convert HSL string to CSS
const hslToCss = (hsl: string | undefined): string | undefined => {
  if (!hsl || hsl.trim() === "") return undefined;
  return hsl;
};

// Simple markdown-like rendering for preview
const renderContent = (content: string, fontMono?: string) => {
  const parts: React.ReactNode[] = [];
  let key = 0;

  // Process code blocks first
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    // Add text before code block
    if (match.index > lastIndex) {
      parts.push(
        <span key={key++}>
          {renderInlineContent(content.slice(lastIndex, match.index))}
        </span>
      );
    }

    // Add code block
    parts.push(
      <pre
        key={key++}
        className="my-3 overflow-x-auto rounded-lg bg-muted p-3 text-sm"
        style={{ fontFamily: fontMono || "var(--font-mono)" }}
      >
        <code className="text-foreground">{match[2]}</code>
      </pre>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(
      <span key={key++}>
        {renderInlineContent(content.slice(lastIndex))}
      </span>
    );
  }

  return parts.length > 0 ? parts : renderInlineContent(content);
};

// Render inline markdown (bold, italic, inline code)
const renderInlineContent = (text: string): React.ReactNode => {
  // Split by newlines and process each line
  return text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line
        .split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/)
        .map((part, j) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith("*") && part.endsWith("*")) {
            return <em key={j} className="italic">{part.slice(1, -1)}</em>;
          }
          if (part.startsWith("`") && part.endsWith("`")) {
            return (
              <code key={j} className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">
                {part.slice(1, -1)}
              </code>
            );
          }
          return part;
        })}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

export function ChatPreview({ values, isDark = false, className }: ChatPreviewProps) {
  const { t } = useTranslation();
  
  // Mock chat messages for preview - localized
  const mockMessages = useMemo(() => [
    {
      id: "1",
      role: "user" as const,
      content: t("preview.messages.userQuestion"),
    },
    {
      id: "2",
      role: "assistant" as const,
      content: t("preview.messages.assistantResponse"),
    },
    {
      id: "3",
      role: "user" as const,
      content: t("preview.messages.userFollowup"),
    },
  ], [t]);

  // Generate CSS variables from branding values
  const previewStyles = useMemo(() => {
    const prefix = isDark ? "dark_" : "";
    const getVal = (key: string) => hslToCss(values[`${prefix}${key}` as keyof BrandingValues] as string);
    
    return {
      "--background": getVal("background") || (isDark ? "224 71% 4%" : "0 0% 100%"),
      "--foreground": getVal("foreground") || (isDark ? "213 31% 91%" : "222.2 47.4% 11.2%"),
      "--card": getVal("card") || (isDark ? "224 71% 4%" : "0 0% 100%"),
      "--card-foreground": getVal("card_foreground") || (isDark ? "213 31% 91%" : "222.2 47.4% 11.2%"),
      "--primary": getVal("primary") || (isDark ? "210 40% 98%" : "222.2 47.4% 11.2%"),
      "--primary-foreground": getVal("primary_foreground") || (isDark ? "222.2 47.4% 1.2%" : "210 40% 98%"),
      "--secondary": getVal("secondary") || (isDark ? "222.2 47.4% 11.2%" : "210 40% 96.1%"),
      "--secondary-foreground": getVal("secondary_foreground") || (isDark ? "210 40% 98%" : "222.2 47.4% 11.2%"),
      "--muted": getVal("muted") || (isDark ? "223 47% 11%" : "210 40% 96.1%"),
      "--muted-foreground": getVal("muted_foreground") || (isDark ? "215.4 16.3% 56.9%" : "215.4 16.3% 46.9%"),
      "--accent": getVal("accent") || (isDark ? "216 34% 17%" : "210 40% 96.1%"),
      "--accent-foreground": getVal("accent_foreground") || (isDark ? "210 40% 98%" : "222.2 47.4% 11.2%"),
      "--border": getVal("border") || (isDark ? "216 34% 17%" : "214.3 31.8% 91.4%"),
      "--input": getVal("input") || (isDark ? "216 34% 17%" : "214.3 31.8% 91.4%"),
      "--ring": getVal("ring") || (isDark ? "216 34% 17%" : "215 20.2% 65.1%"),
      // Sidebar specific colors - check 'sidebar' first (from presets), then 'sidebar_background'
      "--sidebar-background": getVal("sidebar") || getVal("sidebar_background") || (isDark ? "246 30% 15%" : "246 50% 98%"),
      "--sidebar-foreground": getVal("sidebar_foreground") || (isDark ? "246 15% 96%" : "246 72% 26%"),
      "--sidebar": getVal("sidebar") || getVal("sidebar_background") || (isDark ? "246 30% 15%" : "246 50% 98%"),
      "--sidebar-primary": getVal("sidebar_primary") || getVal("primary") || (isDark ? "246 72% 65%" : "246 72% 65%"),
      "--sidebar-primary-foreground": getVal("sidebar_primary_foreground") || getVal("primary_foreground") || (isDark ? "0 0% 100%" : "0 0% 100%"),
      "--sidebar-accent": getVal("sidebar_accent") || (isDark ? "246 30% 6%" : "246 30% 95%"),
      "--sidebar-accent-foreground": getVal("sidebar_accent_foreground") || (isDark ? "246 15% 96%" : "246 72% 25%"),
      "--sidebar-border": getVal("sidebar_border") || getVal("border") || (isDark ? "246 20% 26%" : "246 20% 90%"),
      "--sidebar-ring": getVal("sidebar_ring") || getVal("ring") || (isDark ? "323 52% 57%" : "323 52% 57%"),
      "--radius": values.radius || "0.75rem",
      "--font-sans": values.font_sans || "Inter, ui-sans-serif, system-ui, sans-serif",
      "--font-mono": values.font_mono || "JetBrains Mono, ui-monospace, monospace",
    } as React.CSSProperties;
  }, [values, isDark]);

  return (
    <div
      className={cn(
        "flex rounded-lg border overflow-hidden shadow-lg",
        isDark ? "dark" : "",
        className
      )}
      style={{
        ...previewStyles,
        backgroundColor: `hsl(var(--background))`,
        color: `hsl(var(--foreground))`,
        fontFamily: "var(--font-sans)",
        borderColor: `hsl(var(--border))`,
        height: "520px",
      }}
    >
      {/* Sidebar - matches ChatSidebar */}
      <div 
        className="flex w-52 flex-col border-r"
        style={{ 
          backgroundColor: `hsl(var(--sidebar-background))`,
          borderColor: `hsl(var(--sidebar-border))`,
        }}
      >
        {/* Sidebar Header */}
        <div className="p-2 pt-3">
          {/* Nav Items */}
          <div className="flex flex-col gap-0.5">
            <SidebarItem isActive>
              <MessageCirclePlus className="h-4 w-4" />
              <span>{t("chat.newChat")}</span>
            </SidebarItem>
            <SidebarItem>
              <Search className="h-4 w-4" />
              <span>{t("chat.search")}</span>
            </SidebarItem>
          </div>
        </div>
        
        {/* Sidebar Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-2">
          {/* Favorites Section */}
          <div className="py-2">
            <p 
              className="mb-1 px-2 text-xs font-medium"
              style={{ color: `hsl(var(--muted-foreground))` }}
            >
              {t("chat.favorites")}
            </p>
            <div className="flex flex-col gap-0.5">
              <SidebarItem>
                <Star className="h-3.5 w-3.5" style={{ color: `hsl(var(--muted-foreground))` }} />
                <span className="truncate text-xs">{t("preview.sidebar.favoriteChat1")}</span>
              </SidebarItem>
            </div>
          </div>
          
          {/* Chats Section */}
          <div className="py-2">
            <p 
              className="mb-1 px-2 text-xs font-medium"
              style={{ color: `hsl(var(--muted-foreground))` }}
            >
              {t("chat.chats")}
            </p>
            <div className="flex flex-col gap-0.5">
              {[t("preview.sidebar.chat1"), t("preview.sidebar.chat2"), t("preview.sidebar.chat3")].map((chat, idx) => (
                <SidebarItem key={idx}>
                  <MessageSquare className="h-3.5 w-3.5" style={{ color: `hsl(var(--muted-foreground))` }} />
                  <span className="truncate text-xs">{chat}</span>
                </SidebarItem>
              ))}
            </div>
          </div>
        </div>
        
        {/* Sidebar Footer */}
        <div 
          className="border-t p-2"
          style={{ borderColor: `hsl(var(--sidebar-border))` }}
        >
          <SidebarItem>
            <CircleHelp className="h-4 w-4" style={{ color: `hsl(var(--muted-foreground))` }} />
            <span>{t("chat.help")}</span>
          </SidebarItem>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col">
        {/* Messages Area - matches the real chat layout */}
        <div 
          className="flex-1 overflow-y-auto min-h-0"
          style={{ backgroundColor: `hsl(var(--background))` }}
        >
          <div className="flex flex-col gap-1 p-4">
            {mockMessages.map((message) => (
                <div key={message.id} className="group flex flex-col gap-0">
                  <div
                    className={cn(
                      "text-wrap break-words rounded-xl p-3 text-sm",
                      message.role === "user"
                        ? "ml-auto max-w-[85%]"
                        : "assistant-message-bubble-card mr-auto w-full py-0 pb-1"
                    )}
                    style={message.role === "user" ? {
                      backgroundColor: `hsl(var(--primary) / 0.1)`,
                      color: `hsl(var(--foreground))`,
                    } : {
                      color: `hsl(var(--foreground))`,
                    }}
                  >
                    {message.role === "user" ? (
                      message.content
                    ) : (
                      <div className="leading-relaxed">
                        {renderContent(message.content, values.font_mono)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        {/* Input Area - matches ChatInputArea exactly */}
        <div 
          className="p-4 pt-2"
          style={{ backgroundColor: `hsl(var(--background))` }}
        >
          <div 
            className="relative rounded-md border pb-10 pt-1 shadow transition-all duration-200"
            style={{ borderColor: `hsl(var(--border))` }}
          >
            <Textarea
              placeholder={t("preview.placeholder")}
              rows={1}
              className="max-h-[200px] min-h-fit flex-1 resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ 
                color: `hsl(var(--foreground))`,
                fontFamily: "var(--font-sans)",
                backgroundColor: "transparent",
              }}
              disabled
            />
            <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 p-1">
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled
                  >
                    <Plus className="h-5 w-5" style={{ color: `hsl(var(--muted-foreground))` }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled
                  >
                    <Settings className="h-5 w-5" style={{ color: `hsl(var(--muted-foreground))` }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled
                  >
                    <Bot className="h-5 w-5" style={{ color: `hsl(var(--muted-foreground))` }} />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    className="h-9 min-w-9 rounded-lg shadow-md"
                    style={{ 
                      backgroundColor: `hsl(var(--primary))`, 
                      color: `hsl(var(--primary-foreground))` 
                    }}
                    disabled
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <p 
            className="mt-2 text-center text-xs"
            style={{ color: `hsl(var(--muted-foreground))` }}
          >
            {t("preview.proTip")}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ChatPreview;
